import {Component, Inject, Input, OnInit, PLATFORM_ID} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent, LiveResultHeadingType} from "@components";
import {
  EventItem,
  Race,
  Lookups,
  Country,
  PagedFilter,
  Pages,
  Gender,
  CompetitorFilter,
  CompetitorResults,
  RaceSettings, CategoryItem,
  FullScreenMode
} from "@model";
import {LiveControlService} from "@services";
import {debounceTime, distinctUntilChanged} from "rxjs";
import {environment} from "@environment";
import {LocalizedDatePipe} from "@pipes";
import {isPlatformBrowser, Location} from "@angular/common";

@Component({
  selector: 'app-live-results-race-details',
  templateUrl: './live-results-race-details.component.html',
  styleUrls: ['./live-results-race-details.component.scss'],
})
export class LiveResultsRaceDetailsComponent extends AbstractComponent implements OnInit {
  @Input() race: Race | undefined = undefined;
  @Input() event: EventItem | undefined = undefined;
  @Input() isFullScreen: boolean = false;
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input({required: true}) genders: Country[] = [];
  @Input() liveResult: CompetitorResults[] | undefined = undefined
  @Input() measurementType: number | undefined = undefined;
  @Input() categories: CategoryItem[] = [];

  image: string = `${environment.assetsImagesPath}/assets/img/carousel/live_results.png`;

  name: string = "";
  gender: number | undefined = undefined;
  category: number | undefined = undefined;
  overall: string | undefined = undefined;
  distance: number | undefined = undefined;
  queryParamsFirstChange: boolean = true;

  localizedDatePipe: LocalizedDatePipe;
  fullScreenMode: FullScreenMode = FullScreenMode.FULL;
  competitorLiveResults: CompetitorResults[] = [];
  isRoundRace: boolean = false;
  finishedCount: number = 0;
  startedCount: number = 0;

  liveResultMale: CompetitorResults[] | undefined = undefined;
  liveResultFemale: CompetitorResults[] | undefined = undefined;
  filterCategories: CategoryItem[] = [];
  filterCategoriesLabels: string[] = [];

  isBrowser: boolean;

  constructor(private liveService: LiveControlService, private route: ActivatedRoute, private router: Router,
              public translocoService: TranslocoService, private location: Location,
              @Inject(PLATFORM_ID) private platformId: Object) {
    super();
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.localizedDatePipe = new LocalizedDatePipe(translocoService);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.setFilterCategoriesAndLabels(this.categories);
      this.subscribeToRouteDataChange();
      this.measurementType = this.race?.raceSettings?.measurementType;
      this.isRoundRace = (RaceSettings.isRoundRace(this.race?.raceSettings?.minimumLapDuration) || Race.isBackyardUltra(this.race?.sport));
      this.subscriptions.push(this.liveService.liveResults.subscribe(val => {
        this.handleExploreResult(val, "all")
      }));
      this.subscriptions.push(this.liveService.liveResultsFemale.subscribe(val => {
        this.handleExploreResult(val, "female")
      }));
      this.subscriptions.push(this.liveService.liveResultsMale.subscribe(val => {
        this.handleExploreResult(val, "male")
      }));
      this.subscriptions.push(this.liveService.finishedCount.subscribe(val => {
        this.finishedCount = val ?? 0;
      }));
      this.subscriptions.push(this.liveService.startedCount.subscribe(val => {
        this.startedCount = val ?? 0;
      }));
    }
  }

  setFilterCategoriesAndLabels(categories: CategoryItem[]) {
    if (categories) {
      this.filterCategories = categories;
      this.filterCategoriesLabels = this.filterCategories.map((category: CategoryItem) => category.code ?? "-");
    }
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
        distinctUntilChanged()).subscribe((queryParams: any) => {
        this.name = queryParams.name ? queryParams.name : "";
        this.gender = queryParams.gender ? +queryParams.gender : undefined;
        this.category = queryParams.category ? +queryParams.category : undefined;
        this.initFilterCategories(this.gender);
        this.liveService.startFetchingResults(this.race, this.createFilter());
      })
    );
  }

  handleExploreResult(liveResults: CompetitorResults[] | null | undefined, type: "all"|"female"|"male"): void {
    switch (type) {
      case "all":
        this.competitorLiveResults = liveResults ? [...liveResults] : [];
        break;
      case "female":
        this.liveResultFemale = liveResults ? [...liveResults] : [];
        break;
      case "male":
        this.liveResultMale = liveResults ? [...liveResults] : [];
        break;
    }
  }

  handleInput(value: string) {
    this.name = value.trim() ?? "";
    this.changeQueryParams();
  }

  handleGenderSelection(value?: number): void {
    this.initFilterCategories(value);
    if (value === this.gender || !value) {
      this.gender = undefined;
    } else {
      this.gender = value;
    }
    this.changeQueryParams()
  }

  handleCategorySelection(value?: any): void {
    if (value === this.category || !value) {
      this.category = undefined;
    } else {
      this.category = value;
    }
    this.changeQueryParams()
  }

  initFilterCategories(value?: any) {
    if (value === Gender.MALE) {
      this.setFilterCategoriesAndLabels(this.categories.filter((category: CategoryItem) => category.gender === Gender.MALE));
    } else if (value === Gender.FEMALE) {
      this.setFilterCategoriesAndLabels(this.categories.filter((category: CategoryItem) => category.gender === Gender.FEMALE));
    } else {
      this.setFilterCategoriesAndLabels(this.categories);
    }
  }

  createFilter() {
    return new PagedFilter<CompetitorFilter>(new Pages(), new CompetitorFilter(
      this.race?.eventId, this.race?.id, this.category ? [this.category] : undefined, this.name !== "" ? this.name : undefined, this.gender
    ))
  }

  private changeQueryParams(): void {
    let queryParams: { [key: string]: any } = {};
    queryParams["eventId"] = this.event?.id;
    queryParams["raceId"] = this.race?.id
    if (this.gender) {
      queryParams['gender'] = this.gender;
    }
    if (this.category) {
      queryParams['category'] = this.category;
    }
    if (this.name && this.name.length > 0) {
      queryParams['name'] = this.name
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true
    });
  }

  toggleView() {
    if (this.fullScreenMode === FullScreenMode.SPLIT) {
      this.fullScreenMode = FullScreenMode.FULL;
    } else {
      this.fullScreenMode = FullScreenMode.SPLIT;
    }
    this.liveService.stopFetching();
    this.liveService.startFetchingResults(this.race, this.createFilter(), this.fullScreenMode === FullScreenMode.FULL);
  }

  protected readonly LiveResultHeadingType = LiveResultHeadingType;
  protected readonly FullScreenMode = FullScreenMode;
  protected readonly Gender = Gender;

  back(): void {
    this.location.back();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.liveService.clearAll();
  }
}
